import copy from 'clipboard-copy';
import * as React from 'react';
import { useState } from 'react';
import { useUser } from '../../hooks/use-user';
import {
  ArrowLeftIcon,
  Box,
  Button,
  CopyIcon,
  FacebookIcon,
  Image,
  Link,
  LinkedinIcon,
  Logo,
  TUILink,
  Text,
  TwitterIcon,
} from '../../ui';
import apiRequest from '../../utils/api-request';

const Referrals = () => {
  const user = useUser();

  if (!user) return null;

  // Legacy users won't have a referralId. Create one for them.
  if (!user.referralId) {
    apiRequest('referrals', {
      method: 'POST',
      json: { name: user.firstName },
    });
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <Header />
      <Box
        sx={{
          display: 'flex',
          px: 4,
          pt: 5,
          maxWidth: 908,
          mx: 'auto',
          flexDirection: ['column', 'column', 'row'],
        }}
      >
        <PromoText
          headerText={
            <>
              Give $10.
              <br />
              Get $10.
            </>
          }
        />
        <Links referralId={user.referralId} />
      </Box>
      <Box sx={{ position: 'absolute', bottom: 4, left: 4 }}>
        <Link to="/">
          <Logo />
        </Link>
      </Box>
    </Box>
  );
};

function PromoText({ headerText }: { headerText: React.ReactNode }) {
  return (
    <Box
      sx={{
        backgroundSize: 'cover',
        width: ['100%', '100%', '58%'],
        height: [330, 520],
        backgroundImage: `url(https://assets.standardresume.co/f_auto,q_auto,w_1000,h_1000,c_fill/v1/app/referrals/gold_zkvb1p.jpg)`,
        py: [30, 40],
        pl: [16, 40],
        pr: 16,
      }}
    >
      <Image
        src="https://assets.standardresume.co/f_auto,q_auto/v1/app/referrals/icons_ptcs8c.png"
        sx={{ width: 222, height: 80, display: ['none', 'block'], mb: 5 }}
      />
      <Text
        sx={{
          fontWeight: 700,
          fontSize: [32, 36],
          lineHeight: 1.2,
          mb: 5,
          maxWidth: 450,
        }}
      >
        {headerText}
      </Text>
      <Text sx={{ maxWidth: 370, fontSize: 18 }}>
        Invite your friends and family to Standard Resume, and you’ll each get
        $10 off when they subscribe to Pro.
      </Text>
    </Box>
  );
}

function Links({ referralId }: { referralId: string | undefined }) {
  const [success, setSuccess] = useState(false);
  const referralUrl = referralId
    ? `${process.env.PUBLIC_WEBSITE_URL || ''}/friends/${referralId}`
    : 'Loading...';

  const socialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    color: 'blue',
    fontWeight: 'bold',
    mb: 4,
    textDecoration: 'none',
  };
  return (
    <Box
      sx={{
        width: ['100%', '100%', '42%'],
        pl: 5,
        mt: [40, 40, 52],
        mb: 150,
      }}
    >
      <Button
        onClick={() => copyUrl(referralUrl, setSuccess)}
        variant="link"
        pl={0}
      >
        <CopyIcon size={24} mr={2} color="blue" />
        <Text
          sx={{
            color: 'blue',
            pl: 2,
            fontWeight: 'bold',
          }}
        >
          {success ? 'Done!' : 'Copy invite link'}
        </Text>
      </Button>
      <Text sx={{ color: 'black70', mb: 60 }}>{referralUrl}</Text>
      <TUILink
        href={getTweetUrl(referralUrl)}
        sx={socialLinkStyles}
        target="_blank"
        rel="noopener noreferrer"
      >
        <TwitterIcon color="blue" mr={2} />
        Twitter
      </TUILink>
      <TUILink
        href={getFacebookUrl(referralUrl)}
        sx={socialLinkStyles}
        target="_blank"
        rel="noopener noreferrer"
      >
        <FacebookIcon color="blue" mr={2} />
        Facebook
      </TUILink>
      <TUILink
        href={getLinkedinUrl(referralUrl)}
        sx={socialLinkStyles}
        target="_blank"
        rel="noopener noreferrer"
      >
        <LinkedinIcon color="blue" mr={2} />
        LinkedIn
      </TUILink>
      <Text sx={{ mt: 60, color: 'black50', maxWidth: 340, fontSize: 14 }}>
        We’ll apply a $10 credit to your account each time a friend subscribes
        to Pro. Visit our{' '}
        <TUILink
          href={`${
            process.env.PUBLIC_WEBSITE_URL || ''
          }/frequently-asked-questions`}
          sx={{ color: 'inherit' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          FAQs
        </TUILink>{' '}
        for more information.
      </Text>
    </Box>
  );
}

function Header() {
  return (
    <Box sx={{ position: 'relative' }}>
      <Link
        to="/resumes"
        sx={{ position: 'absolute', top: 12, left: 3, height: 32, width: 32 }}
      >
        <ArrowLeftIcon />
      </Link>
      <Text
        sx={{
          variant: 'text.baseEmphasis',
          lineHeight: '32px',
          display: 'block',
          textAlign: 'center',
          py: 3,
        }}
      >
        Standard Resume Friends
      </Text>
    </Box>
  );
}

function copyUrl(url: string, setSuccess: (arg0: boolean) => void) {
  copy(url).then(() => setSuccess(true));
  window.setTimeout(() => setSuccess(false), 1000 * 10);
}

function getTweetUrl(url: string) {
  const tweet = `I used @StandardResume to create my resume and loved it. If you make a resume using my link, you'll get $10 off.`;
  return `https://twitter.com/intent/tweet?url=${window.encodeURIComponent(
    url
  )}&text=${window.encodeURIComponent(tweet)}`;
}

function getFacebookUrl(url: string) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    url
  )}`;
}

function getLinkedinUrl(url: string) {
  return `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
    url
  )}`;
}

export default Referrals;
